import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CustomerInfo, Package } from "@revenuecat/purchases-js";
import { toast } from "sonner";
import {
  getActivePlan,
  getRevenueCatErrorMessage,
  initRevenueCat,
  isUserCancelled,
  REVENUECAT_OFFERING_ID,
} from "@/lib/revenuecat";

const rcKeys = {
  customerInfo: (appUserId: string) => ["revenuecat", "customer-info", appUserId] as const,
  offerings: (appUserId: string) => ["revenuecat", "offerings", appUserId] as const,
};

/**
 * Estado de suscripción de RevenueCat para la cuenta actual:
 * customerInfo, entitlement vendeplay_pro, offering actual y acciones de compra.
 */
export function useRevenueCat(appUserId: string | undefined, customerEmail?: string) {
  const queryClient = useQueryClient();
  const enabled = !!appUserId;

  const customerInfoQuery = useQuery({
    queryKey: rcKeys.customerInfo(appUserId ?? ""),
    queryFn: async () => (await initRevenueCat(appUserId!)).getCustomerInfo(),
    enabled,
    staleTime: 60_000,
  });

  const offeringsQuery = useQuery({
    queryKey: rcKeys.offerings(appUserId ?? ""),
    queryFn: async () => (await initRevenueCat(appUserId!)).getOfferings(),
    enabled,
    staleTime: 5 * 60_000,
  });

  const onPurchased = (customerInfo: CustomerInfo) => {
    queryClient.setQueryData(rcKeys.customerInfo(appUserId!), customerInfo);
    // El backend se sincroniza vía webhook de RevenueCat; refrescamos su vista también.
    queryClient.invalidateQueries({ queryKey: ["subscription"] });
    const active = getActivePlan(customerInfo);
    if (active) {
      toast.success(`¡Ya tienes el plan ${active.plan.name}!`);
    }
  };

  const onPurchaseError = (error: unknown) => {
    if (isUserCancelled(error)) return;
    console.error("RevenueCat purchase error:", error);
    toast.error(getRevenueCatErrorMessage(error));
  };

  const paywallMutation = useMutation({
    mutationFn: async () => {
      const purchases = await initRevenueCat(appUserId!);
      return purchases.presentPaywall({ customerEmail });
    },
    onSuccess: ({ customerInfo }) => onPurchased(customerInfo),
    onError: onPurchaseError,
  });

  const purchaseMutation = useMutation({
    mutationFn: async (rcPackage: Package) => {
      const purchases = await initRevenueCat(appUserId!);
      return purchases.purchase({ rcPackage, customerEmail, selectedLocale: "es" });
    },
    onSuccess: ({ customerInfo }) => onPurchased(customerInfo),
    onError: onPurchaseError,
  });

  const customerInfo = customerInfoQuery.data;

  return {
    customerInfo,
    activePlan: getActivePlan(customerInfo),
    currentOffering:
      (REVENUECAT_OFFERING_ID && offeringsQuery.data?.all[REVENUECAT_OFFERING_ID]) ||
      offeringsQuery.data?.current ||
      null,
    isLoading: customerInfoQuery.isLoading || offeringsQuery.isLoading,
    error: customerInfoQuery.error ?? offeringsQuery.error,
    presentPaywall: paywallMutation.mutate,
    purchasePackage: purchaseMutation.mutate,
    isPurchasing: paywallMutation.isPending || purchaseMutation.isPending,
    refresh: () => queryClient.invalidateQueries({ queryKey: ["revenuecat"] }),
  };
}
